/* eslint react/require-default-props: ["error", { "functions": "defaultArguments" }] */
import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { VideoJsPlayer } from 'video.js';
import classNames from 'classnames';
import VideoPlayer, { type VideoPlayerProps } from '../VideoPlayer';
import { Button } from '@/atoms';
import { colors, black, heavy, bold } from '@/styles';
import { Replay } from '@/svgs';
import type { StyledComponent } from '@/helpers';
import { VideoLibraryProps } from '@/organisms';
import { NonUndefined } from '@/functions/typeHelpers';

export type VideoPreviewProps = StyledComponent & {
  canInsert?: boolean;
  clientId: VideoPlayerProps['clientId'];
  enableAudioTrackButton?: VideoPlayerProps['enableAudioTrackButton'];
  enableFullscreenControl?: VideoPlayerProps['enableFullscreenControl'];
  environment: VideoPlayerProps['environment'];
  isRebrand?: boolean;
  layout?: VideoLibraryProps['layout'];
  onCancel: React.MouseEventHandler;
  onInsert: React.MouseEventHandler;
  showInsertButton?: boolean;
  sources: VideoPlayerProps['sources'];
  subscriptionId: VideoPlayerProps['subscriptionId'];
  subscriptionStatus: VideoPlayerProps['subscriptionId'];
  thumbUrl: NonUndefined<VideoPlayerProps['thumbUrl']>;
};

/**
 * VideoPreview component
 */
function VideoPreview({
  canInsert = true,
  className,
  clientId,
  enableAudioTrackButton = false,
  enableFullscreenControl = true,
  environment,
  layout = 'default',
  onCancel,
  onInsert,
  showInsertButton = true,
  sources,
  subscriptionId,
  subscriptionStatus,
  thumbUrl,
  isRebrand = false,
}: VideoPreviewProps) {
  const buttonColor = useMemo(
    () => (layout === 'default' ? 'battleship' : 'silver-sand-alt'),
    [layout],
  );
  const buttonRectangleStyle = useMemo(() => layout === 'compact', [layout]);

  const endScreenProps = useMemo(
    () => ({
      buttons: [
        {
          text: 'Replay',
          callback: (player: VideoJsPlayer) => {
            player.ready(() => player.play());
          },
          element: Replay,
          name: 'replay',
        },
      ],
    }),
    [],
  );

  return (
    <div
      className={classNames(className, `layout--${layout}`, {
        'brand--new': isRebrand,
      })}
    >
      <div className="header">Preview Video</div>
      {!isRebrand && (
        <div className="player">
          <VideoPlayer
            className="video-preview"
            thumbUrl={thumbUrl}
            sources={sources}
            endScreenProps={endScreenProps}
            enableFullscreenControl={enableFullscreenControl}
            enableAudioTrackButton={enableAudioTrackButton}
            clientId={clientId}
            subscriptionId={subscriptionId}
            subscriptionStatus={subscriptionStatus}
            environment={environment}
          />
        </div>
      )}

      <div className="buttons">
        <Button
          className="button--cancel"
          isRebrand={isRebrand}
          rectangle={isRebrand || buttonRectangleStyle}
          size="small"
          label="Cancel"
          onClick={onCancel}
          color={isRebrand ? colors.electricBlue : buttonColor}
          outline
        />
        {layout === 'default' && showInsertButton && (
          <Button
            isRebrand={isRebrand}
            rectangle={isRebrand}
            size="small"
            label="Insert"
            onClick={onInsert}
            color={isRebrand ? colors.electricBlue : colors.kermit}
            disabled={!canInsert}
          />
        )}
      </div>

      {isRebrand && (
        <div className="player">
          <VideoPlayer
            className="video-preview"
            thumbUrl={thumbUrl}
            sources={sources}
            endScreenProps={endScreenProps}
            enableFullscreenControl={enableFullscreenControl}
            enableAudioTrackButton={enableAudioTrackButton}
            clientId={clientId}
            subscriptionId={subscriptionId}
            subscriptionStatus={subscriptionStatus}
            environment={environment}
          />
        </div>
      )}
    </div>
  );
}

const styledVideoPreview = styled(VideoPreview)`
  .header {
    ${black()};
    box-shadow: 0 0.1875rem 0.375rem ${colors.boxShadow};
    color: ${colors.battleship};
    font-size: 1.125rem;
    height: 4.6875rem;
    padding: 1.8125rem;
    text-align: left;
  }

  .player {
    padding: 1.5rem;
    margin: 0 auto;
    max-width: 34.1875rem;
  }

  .buttons {
    padding-bottom: 1em;

    button {
      margin: 0 0.5rem;
      width: 6.25rem;
    }
  }

  &.layout--compact {
    .header {
      font-size: ${15 / 16}rem;
      box-shadow: none;
      text-align: center;
      padding-bottom: ${19 / 16}rem;
      height: auto;
    }

    .player {
      padding-top: 0;
    }

    .buttons {
      button {
        ${'' /* 3rem is padding = (1.5rem x 2) */}
        width: calc(100% - 3rem);
        border-width: ${1 / 16}rem;

        span {
          ${heavy()};
          font-size: ${14 / 16}rem;
          padding: 0.5rem 0;
        }
      }
    }
  }

  &.brand--new {
    .header {
      ${bold()};
    }

    .player {
      padding: unset;
      margin: 0 auto;
      max-width: 34.1875rem;
    }

    .buttons {
      display: flex;
      justify-content: center;
      max-width: 34.1875rem;
      margin: 0 auto;
      padding: 1.4375rem 0;
      gap: 0.75rem;

      button {
        margin: 0;
        width: 6.25rem;
      }

      .button--cancel:hover {
        color: ${colors.white};
        background: ${colors.electricBlue};
        border-color: ${colors.electricBlue};
      }
    }

    @media screen and (max-width: 360px) {
      .buttons {
        padding: 1.4375rem 0;
      }
    }

    &.layout--compact {
      .buttons {
        padding: 1.4375rem 0;
        max-width: 34.1875rem;
        margin: 0 auto;

        button {
          width: 100%;

          span {
            ${bold()};
          }
        }
      }
    }
  }
`;

export { styledVideoPreview as VideoPreview };
